import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  FormControl, TextField, Select, InputLabel, OutlinedInput,
  MenuItem, Checkbox, ListItemText, Snackbar,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';

import { coreApi } from '../../utils/axios';
import { HealtheraButton, SecondaryButton } from '../../healtheraComponents';
import imageUrl from  '../../resources/image.svg';
import videoUrl from  '../../resources/video.svg';

import ConfirmationDialog from './ConfirmationDialog';

function AnnouncementForm() {
  const now = new Date().toISOString();
  const [formattedCurrentTimeDate] = now.match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/);

  const [loading, setLoading] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [error, setError] = useState('');
  const [title, setTitle] = useState('');
  const [dbCategories, setDbCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [description, setDescription] = useState('');
  const [created, setCreated] = useState(formattedCurrentTimeDate);
  const [files, setFiles] = useState([]);

  const imageInputRef = useRef();
  const videoInputRef = useRef();

  

  useEffect(() => {
    (async() => {
      const response = await coreApi.get('/announcements/categories');
      setDbCategories(response.data.data);
    })();
  }, [])

  function updateTitle(e) {
    setTitle(e.target.value);
  }

  function handleImageUpload() {
    imageInputRef.current.click();
  }

  function handleVideoUpload() {
    videoInputRef.current.click();
  }

  async function uploadFile(e) {
    const file = e.target.files[0];
    file.src = URL.createObjectURL(file);
    setFiles(files => files.concat(file));
  }

  async function submitAnnouncement() {
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('categories', categories.map(cat => cat._id));
      formData.append('description', description);
      formData.append('created', created);
      for (const file of files) {
        formData.append('files', file);
      }

      const response = await coreApi.post('/announcements', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response.data?.error) {
        setError(response.data.error.text || response.data.error);
        return;
      }

      setDialogMessage('Announcement published successfully!');
      resetForm();
    } catch (error) {
      console.error(error.stack);
    }

    setLoading(false);
  }

  function removeMedia(name) {
    setFiles(files => files.filter(file => file.name !== name));
  }

  function resetForm() {
    setTitle('');
    setCategories([]);
    setDescription('');
    setFiles([]);
  }

  async function submitForm() {
    const errors = [];
    if (!title) errors.push('Title is required!');
    if (!categories.length) errors.push('At least one label is required!');
    if (!description) errors.push('Description is required!');
    if (errors.length) {
      alert(errors.join('\n'))
      return;
    }

    await submitAnnouncement();
  }

  return (
    <FormContainer autoComplete="off">
      <ConfirmationDialog message={dialogMessage} handleClose={() => setDialogMessage('')} />
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError('')}
        message={error}
      />
      <TextField variant="outlined" label="Title" value={title} onChange={updateTitle} />
      <FormControl variant="outlined">
        <InputLabel htmlFor="labels">Labels</InputLabel>
        <Select
          multiple
          variant="outlined"
          value={categories}
          onChange={(e) => setCategories(e.target.value)}
          renderValue={() => (
            <CategoriesContainer>
              {categories.map(cat => (
                <Category color={cat.color} key={cat._id}>
                  {cat.title}
                </Category>
              ))}
            </CategoriesContainer>
          )}
          input={
            <OutlinedInput
              labelWidth={50}
              name="Labels"
              id="labels"
            />
          }
        >
          {dbCategories.map(category => (
            <MenuItem key={category._id} value={category}>
              <Checkbox checked={categories.some(cat => cat._id === category._id)} />
              <ListItemText primary={category.title} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Description>
        <TextField
          fullWidth={true}
          placeholder="Write something..."
          multiline={true}
          rows={15}
          style={{padding:'1rem'}}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          InputProps={{
            disableUnderline: true,
          }}
        />
        {files.length > 0 && (
          <FileContainer>
            {files.map(file => (
              <div key={file.name}>
                <DeleteItem onClick={() => removeMedia(file.name)} />
                {/image/.test(file.type) && <img src={file.src} alt="preview" />}
                {/video/.test(file.type) && (
                  <div className="video">
                    <video width="200px" controls>
                      <source src={file.src} type={file.type} />
                    </video>
                  </div>)}
              </div>
            ))}
          </FileContainer>
        )}
        <MediaToolbar>
          <Button title="Upload an image" onClick={handleImageUpload} type="button">
            <MediaInput type="file" accept="image/*" ref={imageInputRef} onChange={uploadFile} />
            <img src={imageUrl} alt="icon" />
          </Button>
          <Button title="Upload a video" onClick={handleVideoUpload} type="button">
            <MediaInput type="file" accept="video/*" ref={videoInputRef} onChange={uploadFile} />
            <img src={videoUrl} alt="icon" />
          </Button>
        </MediaToolbar>
      </Description>
      <Footer>
        <DatePicker>
          <TextField
            type="datetime-local"
            value={created}
            onChange={(e) => setCreated(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </DatePicker>
        <SecondaryButton height="34px" width="120px" onClick={resetForm}>Cancel</SecondaryButton>
        <HealtheraButton
          height="34px" width="120px"
          onClick={submitForm}
          disabled={loading}
        >
          {loading ? 'Uploading...' : 'Post'}
        </HealtheraButton>
      </Footer>
    </FormContainer>
  );
}

export default AnnouncementForm;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Description = styled.div`
  border: 1px solid #c0c0c0;
  border-radius: 4px;
  overflow: hidden;
`;

const MediaToolbar = styled.div`
  width: 100%;
  background-color: #F1F1F1;
  padding: 10px 30px;
  display: flex;
  gap: 30px;
  box-sizing: border-box;
`;

const MediaInput = styled.input`
  display: none;
`;

const Button = styled.button`
  cursor: pointer;
  border: 0;
  padding: 0;
`;

const DatePicker = styled.div`
  border: 1px solid grey;
  border-radius: 15px;
  padding: 0 10px;
  width: fit-content;
  margin-right: auto;
`;

const Footer = styled.div`
  display: flex;
  gap: 1rem;
`;

const FileContainer = styled.div`
  height: 100px;
  border-top: 1px solid grey;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  overflow-x: auto;

  & * {
    position: relative;
    height: 100%;
  }

  & .video {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
    background-color: #e5e5e5;
  }
`;

const CategoriesContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Category = styled.span`
  padding: 0.25rem 0.5rem;
  border: 1px solid ${props => props.color};
  border-radius: 15px;
  color: ${props => props.color};
`;

const DeleteItem = styled(CancelIcon)`
  position: absolute;
  top: -12px;
  right: -12px;
  z-index: 999;
  cursor: pointer;
`;
